import { useEffect, useMemo, useRef, useState, PointerEvent as ReactPointerEvent } from "react";
import GenelDurumHelper from "../../../../helpers/GenelDurumHelper";
import IcerikHelper from "../../../../helpers/IcerikHelper";
import AlertHelper from "../../../../library/helpers/AlertHelper";
import { GuidHelper } from "../../../../library/helpers/GuidHelper";
import DateTimeHelper from "../../../../library/helpers/DateTimeHelper";
import StringHelper from "../../../../library/helpers/StringHelper";
import { HesapSarkiSesKayitNotlarModel } from "../../../../models/businessModels/HesapSarkiSesKayitNotlarModel";
import { drawStrokeOnContext, drawStrokesOnContext } from "../pdfCanvasStrokeRender";
import { createEmptyImageDataUrl, loadImage } from "../pdfCanvasImageUtils";
import { splitLongImageBase64ToPageDataUrls } from "../pdfCanvasPageSplit";
import { exportAnnotatedPagesToLongPngBase64 } from "../pdfCanvasExport";
import { parseNotKoordinat } from "../pdfCanvasNotHelpers";
import { findNotStrokeAtPoint } from "../pdfCanvasHitTest";
import { usePdfMarkHover } from "./usePdfMarkHover";
import {
    pdfCanvasPerfEraserCursorUpdate,
    pdfCanvasPerfFinishStroke,
    pdfCanvasPerfForceRender,
    pdfCanvasPerfPointerMove,
    pdfCanvasPerfRedrawAllEnd,
    pdfCanvasPerfRedrawAllStart,
    pdfCanvasPerfRedrawPageEnd,
    pdfCanvasPerfRedrawPageStart,
    pdfCanvasPerfStartStroke,
} from "../pdfCanvasPerfDebug";
import type {
    PdfCanvasBrushSizeType,
    PdfCanvasEraserCursorState,
    PdfCanvasHistoryItem,
    PdfCanvasMarkHoverState,
    PdfCanvasPageMeta,
    PdfCanvasPoint,
    PdfCanvasStroke,
    PdfCanvasToolType,
} from "../pdfCanvasTypes";

interface IUsePdfAnnotatorParams {
    imageUrlList: { uri: string }[] | null,
    initialImageIcerikId?: string | null,
    initialNotlar?: HesapSarkiSesKayitNotlarModel[],
    genelDurumHelper: GenelDurumHelper,
    readOnly: boolean,
    getAudioSure?: () => number,
    playAudioAtSure?: (sure: number) => void,
    seciliRenk: string,
    seciliArac: PdfCanvasToolType,
    fircaBoyut: PdfCanvasBrushSizeType,
    setEraserCursor: React.Dispatch<React.SetStateAction<PdfCanvasEraserCursorState>>,
}

/** İşaret hover hit-test'inde fırça yarıçapına eklenen pay (sayfa natural px). */
const MARK_HOVER_TOLERANCE = 2;

export function usePdfAnnotator(params: IUsePdfAnnotatorParams) {
    const [notlar, setNotlar] = useState<HesapSarkiSesKayitNotlarModel[]>(() => params.initialNotlar ?? []);
    const [isDirty, setIsDirty] = useState(false);
    const [version, setVersion] = useState(0);

    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const imageRefList = useRef<(HTMLImageElement | null)[]>([]);
    const canvasRefList = useRef<(HTMLCanvasElement | null)[]>([]);
    const pageMetaRef = useRef<PdfCanvasPageMeta[]>([]);
    const initialPageDataUrlListRef = useRef<string[]>([]);
    const pageStrokeListRef = useRef<PdfCanvasStroke[][]>([]);
    const undoStackRef = useRef<PdfCanvasHistoryItem[]>([]);
    const redoStackRef = useRef<PdfCanvasHistoryItem[]>([]);
    const activeStrokeRef = useRef<{ pageIndex: number, stroke: PdfCanvasStroke } | null>(null);
    const pointerDownRef = useRef(false);
    const initialImageCacheRef = useRef<Map<number, { dataUrl: string, image: HTMLImageElement }>>(new Map());
    const activePageRedrawRafRef = useRef<number | null>(null);
    const activePageRedrawPendingRef = useRef<number | null>(null);

    const markHover = usePdfMarkHover();

    const notlarSureyeGore = useMemo(
        () => [...notlar].orderBy(n => n.sure ?? 0),
        [notlar]);

    const forceRender = (source = "unknown") => {
        pdfCanvasPerfForceRender(source);
        setVersion(v => v + 1);
    };

    const invalidateInitialImageCache = () => {
        initialImageCacheRef.current.clear();
    };

    const cancelScheduledActivePageRedraw = () => {
        if (activePageRedrawRafRef.current !== null) {
            cancelAnimationFrame(activePageRedrawRafRef.current);
            activePageRedrawRafRef.current = null;
        }
        activePageRedrawPendingRef.current = null;
    };

    const redrawPagesDirect = async (pageIndices: number[]) => {
        if (pageIndices.length === 0) {
            return;
        }

        const t0 = pdfCanvasPerfRedrawAllStart(version, pageIndices.length);
        const pageResults = [];
        for (const pageIndex of pageIndices) {
            pageResults.push(await redrawCanvas(pageIndex));
        }
        pdfCanvasPerfRedrawAllEnd(t0, pageResults);
    };

    const scheduleActivePageRedraw = (pageIndex: number) => {
        activePageRedrawPendingRef.current = pageIndex;
        if (activePageRedrawRafRef.current !== null) {
            return;
        }

        activePageRedrawRafRef.current = requestAnimationFrame(() => {
            activePageRedrawRafRef.current = null;
            const pendingPageIndex = activePageRedrawPendingRef.current;
            activePageRedrawPendingRef.current = null;
            if (pendingPageIndex !== null) {
                void redrawPagesDirect([pendingPageIndex]);
            }
        });
    };

    const canUndo = () => undoStackRef.current.length > 0;
    const canRedo = () => redoStackRef.current.length > 0;
    const getSelectedRadius = () => params.fircaBoyut === "kucuk" ? 5 : params.fircaBoyut === "buyuk" ? 18 : 10;

    const resetAnnotator = () => {
        setIsDirty(false);
        undoStackRef.current = [];
        redoStackRef.current = [];
        pageMetaRef.current = [];
        initialPageDataUrlListRef.current = [];
        pageStrokeListRef.current = [];
        activeStrokeRef.current = null;
        pointerDownRef.current = false;
        cancelScheduledActivePageRedraw();
        invalidateInitialImageCache();
        markHover.hideNow();
        setNotlar(params.initialNotlar ?? []);
        forceRender("resetAnnotator");
    };

    useEffect(() => {
        setNotlar(params.initialNotlar ?? []);
    }, [params.initialNotlar]);

    useEffect(() => {
        const redrawAll = async () => {
            const pageCount = canvasRefList.current.length;
            const t0 = pdfCanvasPerfRedrawAllStart(version, pageCount);
            const pageResults = [];
            for (let i = 0; i < pageCount; i++) {
                pageResults.push(await redrawCanvas(i));
            }
            pdfCanvasPerfRedrawAllEnd(t0, pageResults);
        };
        redrawAll();
    }, [version]);

    const redrawCanvas = async (pageIndex: number) => {
        const t0 = pdfCanvasPerfRedrawPageStart(pageIndex);
        const canvas = canvasRefList.current[pageIndex];
        const img = imageRefList.current[pageIndex];
        if (!canvas || !img) {
            return pdfCanvasPerfRedrawPageEnd(pageIndex, t0, {
                loadImageMs: 0,
                strokeCount: 0,
                activePointCount: 0,
                canvasW: 0,
                canvasH: 0,
                hadInitialImage: false,
                skipped: true,
                skipReason: !canvas ? "no-canvas" : "no-img",
            });
        }

        const pageMeta = pageMetaRef.current[pageIndex];
        if (!pageMeta) {
            return pdfCanvasPerfRedrawPageEnd(pageIndex, t0, {
                loadImageMs: 0,
                strokeCount: 0,
                activePointCount: 0,
                canvasW: canvas.width,
                canvasH: canvas.height,
                hadInitialImage: false,
                skipped: true,
                skipReason: "no-page-meta",
            });
        }

        canvas.width = img.clientWidth;
        canvas.height = img.clientHeight;

        const ctx = canvas.getContext("2d");
        if (!ctx) {
            return pdfCanvasPerfRedrawPageEnd(pageIndex, t0, {
                loadImageMs: 0,
                strokeCount: 0,
                activePointCount: 0,
                canvasW: canvas.width,
                canvasH: canvas.height,
                hadInitialImage: false,
                skipped: true,
                skipReason: "no-ctx",
            });
        }

        ctx.clearRect(0, 0, canvas.width, canvas.height);

        const initialDataUrl = initialPageDataUrlListRef.current[pageIndex];
        let loadImageMs = 0;
        if (initialDataUrl) {
            const cache = initialImageCacheRef.current;
            const cached = cache.get(pageIndex);
            let initialImage: HTMLImageElement;
            if (cached?.dataUrl === initialDataUrl) {
                initialImage = cached.image;
            } else {
                const loadT0 = performance.now();
                initialImage = await loadImage(initialDataUrl);
                loadImageMs = Math.round(performance.now() - loadT0);
                cache.set(pageIndex, { dataUrl: initialDataUrl, image: initialImage });
            }
            ctx.drawImage(initialImage, 0, 0, canvas.width, canvas.height);
        }

        const strokes = pageStrokeListRef.current[pageIndex] ?? [];
        const strokeScale = {
            scaleX: canvas.width / pageMeta.width,
            scaleY: canvas.height / pageMeta.height,
        };
        drawStrokesOnContext(ctx, strokes, strokeScale);

        const activeStroke = activeStrokeRef.current;
        const activePointCount = activeStroke && activeStroke.pageIndex === pageIndex
            ? activeStroke.stroke.points.length
            : 0;
        if (activePointCount > 0) {
            drawStrokeOnContext(ctx, activeStroke!.stroke, strokeScale);
        }

        return pdfCanvasPerfRedrawPageEnd(pageIndex, t0, {
            loadImageMs,
            strokeCount: strokes.length,
            activePointCount,
            canvasW: canvas.width,
            canvasH: canvas.height,
            hadInitialImage: Boolean(initialDataUrl),
            skipped: false,
        });
    };

    const splitAndSetInitialImage = async (base64: string) => {
        invalidateInitialImageCache();
        initialPageDataUrlListRef.current = await splitLongImageBase64ToPageDataUrls(base64, pageMetaRef.current);
        forceRender("splitAndSetInitialImage");
    };

    const prepareInitialImage = async () => {
        const imageId = params.initialImageIcerikId;
        if (!imageId) {
            invalidateInitialImageCache();
            initialPageDataUrlListRef.current = pageMetaRef.current.map(e => createEmptyImageDataUrl(e.width, e.height));
            forceRender("prepareInitialImage-empty");
            return;
        }

        const base64 = await IcerikHelper.IcerikGetirDirekt(imageId, params.genelDurumHelper);
        await splitAndSetInitialImage(base64);
    };

    const onPageImageLoaded = async (pageIndex: number) => {
        const image = imageRefList.current[pageIndex];
        if (!image) {
            return;
        }

        pageMetaRef.current[pageIndex] = { width: image.naturalWidth, height: image.naturalHeight };
        pageStrokeListRef.current[pageIndex] = pageStrokeListRef.current[pageIndex] ?? [];

        const allPagesReady = (params.imageUrlList?.length ?? 0) > 0 &&
            pageMetaRef.current.length === (params.imageUrlList?.length ?? 0) &&
            pageMetaRef.current.all(e => e && e.width > 0 && e.height > 0);

        if (allPagesReady && initialPageDataUrlListRef.current.length === 0) {
            await prepareInitialImage();
        } else {
            await redrawCanvas(pageIndex);
        }
    };

    const addCanvasNot = (pageIndex: number, point: PdfCanvasPoint, renkKod: string): HesapSarkiSesKayitNotlarModel => {
        const not = new HesapSarkiSesKayitNotlarModel();
        not.id = GuidHelper.NewGuid();
        not.hesapSarkiSesKayitModelId = "";
        not.sayfaNo = pageIndex + 1;
        not.koordinat = JSON.stringify({ x: point.x, y: point.y });
        not.sure = Math.floor(params.getAudioSure?.() ?? 0);
        not.renkKod = renkKod;
        not.yorumlar = "";
        not.olusturan = params.genelDurumHelper.SeciliKullanici.kullaniciAdi;
        not.olusturmaTarih = DateTimeHelper.now();
        setNotlar(prev => [...prev, not]);
        return not;
    };

    const getPoint = (canvas: HTMLCanvasElement, pageIndex: number, event: ReactPointerEvent<HTMLCanvasElement>): PdfCanvasPoint | null => {
        const pageMeta = pageMetaRef.current[pageIndex];
        if (!pageMeta) {
            return null;
        }

        const rect = canvas.getBoundingClientRect();
        const x = ((event.clientX - rect.left) / rect.width) * pageMeta.width;
        const y = ((event.clientY - rect.top) / rect.height) * pageMeta.height;
        return { x, y };
    };

    const updateMarkHover = (canvas: HTMLCanvasElement, pageIndex: number, event: ReactPointerEvent<HTMLCanvasElement>) => {
        const point = getPoint(canvas, pageIndex, event);
        const stroke = point
            ? findNotStrokeAtPoint(pageStrokeListRef.current[pageIndex] ?? [], point, MARK_HOVER_TOLERANCE)
            : null;
        markHover.updateCandidate(stroke?.notId
            ? { pageIndex, stroke, notId: stroke.notId, clientX: event.clientX, clientY: event.clientY }
            : null);
    };

    const onPointerDown = (pageIndex: number, event: ReactPointerEvent<HTMLCanvasElement>) => {
        if (params.readOnly) {
            return;
        }

        markHover.hideNow();
        if (params.seciliArac === "none") {
            return;
        }

        const canvas = canvasRefList.current[pageIndex];
        if (!canvas) {
            return;
        }

        const point = getPoint(canvas, pageIndex, event);
        if (!point) {
            return;
        }

        canvas.setPointerCapture(event.pointerId);
        pointerDownRef.current = true;
        pdfCanvasPerfStartStroke(pageIndex, params.seciliArac);
        activeStrokeRef.current = {
            pageIndex,
            stroke: {
                points: [point],
                color: params.seciliRenk,
                radius: getSelectedRadius(),
                tool: params.seciliArac,
            },
        };
    };

    const onPointerMove = (pageIndex: number, event: ReactPointerEvent<HTMLCanvasElement>) => {
        if (params.readOnly) {
            return;
        }

        const canvas = canvasRefList.current[pageIndex];
        if (!canvas) {
            return;
        }

        if (!pointerDownRef.current) {
            updateMarkHover(canvas, pageIndex, event);
        }

        if (params.seciliArac === "none") {
            return;
        }

        const rect = canvas.getBoundingClientRect();
        pdfCanvasPerfEraserCursorUpdate();
        params.setEraserCursor({
            pageIndex,
            x: event.clientX - rect.left,
            y: event.clientY - rect.top,
            visible: true,
        });

        if (!pointerDownRef.current) {
            return;
        }

        const active = activeStrokeRef.current;
        if (!active || active.pageIndex !== pageIndex) {
            return;
        }

        const point = getPoint(canvas, pageIndex, event);
        if (!point) {
            return;
        }

        active.stroke.points.push(point);
        pdfCanvasPerfPointerMove();
        scheduleActivePageRedraw(pageIndex);
    };

    const finishStroke = () => {
        if (params.readOnly || params.seciliArac === "none") {
            return;
        }

        pointerDownRef.current = false;
        cancelScheduledActivePageRedraw();
        const active = activeStrokeRef.current;
        activeStrokeRef.current = null;
        if (!active) {
            pdfCanvasPerfFinishStroke(0, false);
            return;
        }

        const pageIndex = active.pageIndex;
        const stroke = active.stroke;
        if (stroke.points.length === 1 && stroke.tool === "draw") {
            stroke.points.push({ ...stroke.points[0] });
        }

        if (stroke.points.length < 2) {
            pdfCanvasPerfFinishStroke(stroke.points.length, false);
            void redrawPagesDirect([pageIndex]);
            return;
        }

        const createdNot = stroke.tool === "draw"
            ? addCanvasNot(pageIndex, stroke.points[0], stroke.color)
            : null;
        if (createdNot) {
            stroke.notId = createdNot.id;
            markHover.suppressStroke(stroke);
        }

        pageStrokeListRef.current[pageIndex] = pageStrokeListRef.current[pageIndex] ?? [];
        pageStrokeListRef.current[pageIndex].push(stroke);
        undoStackRef.current.push({ kind: "stroke", pageIndex, stroke, not: createdNot });
        redoStackRef.current = [];
        setIsDirty(true);
        pdfCanvasPerfFinishStroke(stroke.points.length, true);
        void redrawPagesDirect([pageIndex]);
    };

    const exportLongPngBase64 = async (): Promise<string | null> => {
        if ((params.imageUrlList?.length ?? 0) === 0 || pageMetaRef.current.length === 0) {
            return null;
        }

        return exportAnnotatedPagesToLongPngBase64(
            pageMetaRef.current,
            initialPageDataUrlListRef.current,
            pageStrokeListRef.current,
        );
    };

    const removeStrokeByRef = (pageIndex: number, stroke: PdfCanvasStroke) => {
        const list = pageStrokeListRef.current[pageIndex];
        const index = list ? list.indexOf(stroke) : -1;
        if (list && index >= 0) {
            list.splice(index, 1);
        }
    };

    const insertNotAt = (not: HesapSarkiSesKayitNotlarModel, index: number) => {
        setNotlar(prev => {
            const next = [...prev];
            next.splice(Math.min(Math.max(index, 0), next.length), 0, not);
            return next;
        });
    };

    /** Stroke rengi + bağlı notun renkKod'u; sure, yorumlar ve koordinat aynen kalır. Redraw çağırana ait. */
    const applyStrokeColor = (stroke: PdfCanvasStroke, notId: string, renkKod: string) => {
        stroke.color = renkKod;
        setNotlar(prev => prev.map(n => {
            if (n.id !== notId) {
                return n;
            }

            const updated = Object.assign(new HesapSarkiSesKayitNotlarModel(), n);
            updated.renkKod = renkKod;
            return updated;
        }));
    };

    const degistirIsaretRenk = (mark: PdfCanvasMarkHoverState, renkKod: string) => {
        markHover.hideNow();
        markHover.suppressStroke(mark.stroke);
        if (params.readOnly || mark.stroke.color === renkKod) {
            return;
        }

        const oldColor = mark.stroke.color;
        applyStrokeColor(mark.stroke, mark.notId, renkKod);
        undoStackRef.current.push({
            kind: "recolor",
            pageIndex: mark.pageIndex,
            stroke: mark.stroke,
            notId: mark.notId,
            oldColor,
            newColor: renkKod,
        });
        redoStackRef.current = [];
        setIsDirty(true);
        void redrawPagesDirect([mark.pageIndex]);
    };

    const undo = () => {
        const last = undoStackRef.current.pop();
        if (!last) {
            return;
        }

        markHover.hideNow();
        if (last.kind === "stroke") {
            removeStrokeByRef(last.pageIndex, last.stroke);
            if (last.not) {
                const notId = last.not.id;
                setNotlar(prev => prev.filter(n => n.id !== notId));
            }
        } else if (last.kind === "recolor") {
            applyStrokeColor(last.stroke, last.notId, last.oldColor);
        } else {
            // deleteNot: çizimi ve notu geri al
            if (last.stroke) {
                const list = pageStrokeListRef.current[last.pageIndex] ?? [];
                list.splice(Math.min(last.strokeIndex, list.length), 0, last.stroke);
                pageStrokeListRef.current[last.pageIndex] = list;
            }
            insertNotAt(last.not, last.notIndex);
        }

        redoStackRef.current.push(last);
        setIsDirty(undoStackRef.current.length > 0);
        void redrawPagesDirect([last.pageIndex]);
    };

    const redo = () => {
        const last = redoStackRef.current.pop();
        if (!last) {
            return;
        }

        markHover.hideNow();
        if (last.kind === "stroke") {
            pageStrokeListRef.current[last.pageIndex] = pageStrokeListRef.current[last.pageIndex] ?? [];
            pageStrokeListRef.current[last.pageIndex].push(last.stroke);
            if (last.not) {
                const notToAdd = last.not;
                setNotlar(prev => [...prev, notToAdd]);
            }
        } else if (last.kind === "recolor") {
            applyStrokeColor(last.stroke, last.notId, last.newColor);
        } else {
            // deleteNot: silmeyi tekrar uygula
            if (last.stroke) {
                removeStrokeByRef(last.pageIndex, last.stroke);
            }
            const notId = last.not.id;
            setNotlar(prev => prev.filter(n => n.id !== notId));
        }

        undoStackRef.current.push(last);
        setIsDirty(undoStackRef.current.length > 0);
        void redrawPagesDirect([last.pageIndex]);
    };

    const getDisplayBrushRadius = (pageIndex: number) => {
        const canvas = canvasRefList.current[pageIndex];
        const pageMeta = pageMetaRef.current[pageIndex];
        if (!canvas || !pageMeta) {
            return getSelectedRadius();
        }

        const scaleX = canvas.width / pageMeta.width;
        const scaleY = canvas.height / pageMeta.height;
        return getSelectedRadius() * Math.max(scaleX, scaleY);
    };

    const scrollToPage = (pageIndex: number) => {
        const container = scrollContainerRef.current;
        const pageEl = container?.querySelector(`#pdf_canvas_page_${pageIndex}`);
        if (!container || !pageEl) {
            return;
        }

        const pageRect = pageEl.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        const scrollTop = container.scrollTop + (pageRect.top - containerRect.top);
        container.scrollTo({ top: scrollTop, behavior: "smooth" });
    };

    const scrollToNot = (not: HesapSarkiSesKayitNotlarModel) => {
        const pageIndex = not.sayfaNo - 1;
        if (pageIndex < 0 || !params.imageUrlList?.length || pageIndex >= params.imageUrlList.length) {
            return;
        }

        const point = parseNotKoordinat(not.koordinat);
        scrollToPage(pageIndex);

        if (!point) {
            return;
        }

        window.setTimeout(() => {
            const container = scrollContainerRef.current;
            const canvas = canvasRefList.current[pageIndex];
            const pageMeta = pageMetaRef.current[pageIndex];
            if (!container || !canvas || !pageMeta) {
                return;
            }

            const canvasRect = canvas.getBoundingClientRect();
            const containerRect = container.getBoundingClientRect();
            const yRatio = point.y / pageMeta.height;
            const targetYInViewport = canvasRect.top + yRatio * canvasRect.height;
            const delta = targetYInViewport - containerRect.top - container.clientHeight * 0.35;
            container.scrollBy({ top: delta, behavior: "smooth" });
        }, 400);
    };

    const dinleNot = (not: HesapSarkiSesKayitNotlarModel) => {
        scrollToNot(not);
        params.playAudioAtSure?.(not.sure ?? 0);
    };

    const guncelleNotYorumlar = async (not: HesapSarkiSesKayitNotlarModel) => {
        const yorumlar = await AlertHelper.ConfirmWithTextInputMultiLine(
            "Not Yorumları",
            "Yorumlar",
            false,
            StringHelper.nullToEmpty(not.yorumlar),
        );
        if (yorumlar === null) {
            return;
        }

        setNotlar(prev => prev.map(n => {
            if (n.id !== not.id) {
                return n;
            }

            const updated = Object.assign(new HesapSarkiSesKayitNotlarModel(), n);
            updated.yorumlar = yorumlar;
            return updated;
        }));
    };

    const findStrokeForNot = (pageIndex: number, not: HesapSarkiSesKayitNotlarModel): PdfCanvasStroke | null => {
        const list = pageStrokeListRef.current[pageIndex];
        if (!list || list.length === 0) {
            return null;
        }

        let coord: { x: number, y: number } | null = null;
        try {
            coord = not.koordinat ? JSON.parse(not.koordinat) : null;
        } catch {
            coord = null;
        }

        return list.find(stroke =>
            stroke.tool === "draw"
            && stroke.color === not.renkKod
            && (coord == null
                || (stroke.points.length > 0
                    && Math.abs(stroke.points[0].x - coord.x) < 0.5
                    && Math.abs(stroke.points[0].y - coord.y) < 0.5))) ?? null;
    };

    const silNot = (not: HesapSarkiSesKayitNotlarModel) => {
        if (params.readOnly) {
            return;
        }

        markHover.hideNow();
        const pageIndex = (not.sayfaNo ?? 1) - 1;
        const targetStroke = findStrokeForNot(pageIndex, not);
        const list = pageStrokeListRef.current[pageIndex];
        const strokeIndex = targetStroke && list ? list.indexOf(targetStroke) : -1;
        const notIndex = notlar.findIndex(n => n.id === not.id);

        if (targetStroke && list && strokeIndex >= 0) {
            list.splice(strokeIndex, 1);
        }

        undoStackRef.current.push({
            kind: "deleteNot",
            pageIndex,
            not,
            notIndex: notIndex >= 0 ? notIndex : notlar.length,
            stroke: strokeIndex >= 0 ? targetStroke : null,
            strokeIndex,
        });
        redoStackRef.current = [];

        setNotlar(prev => prev.filter(n => n.id !== not.id));
        setIsDirty(undoStackRef.current.length > 0);
        if (targetStroke && strokeIndex >= 0) {
            void redrawPagesDirect([pageIndex]);
        }
    };

    const onCanvasPointerLeave = () => {
        finishStroke();
        markHover.scheduleClose();
        params.setEraserCursor(prev => ({ ...prev, visible: false }));
    };

    const onCanvasPointerEnter = (pageIndex: number) => {
        if (!params.readOnly) {
            params.setEraserCursor(prev => ({ ...prev, pageIndex, visible: true }));
        }
    };

    return {
        notlar,
        notlarSureyeGore,
        isDirty,
        scrollContainerRef,
        initialPageDataUrlListRef,
        pageMetaRef,
        canUndo,
        canRedo,
        undo,
        redo,
        exportLongPngBase64,
        resetAnnotator,
        scrollToPage,
        dinleNot,
        guncelleNotYorumlar,
        silNot,
        hoveredMark: markHover.hoveredMark,
        degistirIsaretRenk,
        onMarkPopoverEnter: markHover.onPopoverEnter,
        onMarkPopoverLeave: markHover.onPopoverLeave,
        getDisplayBrushRadius,
        onPageImageLoaded,
        onPointerDown,
        onPointerMove,
        finishStroke,
        onCanvasPointerLeave,
        onCanvasPointerEnter,
        setImageRef: (pageIndex: number, el: HTMLImageElement | null) => { imageRefList.current[pageIndex] = el; },
        setCanvasRef: (pageIndex: number, el: HTMLCanvasElement | null) => { canvasRefList.current[pageIndex] = el; },
    };
}
